import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import GoogleIcon from '@mui/icons-material/Google';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { login as loginQuery } from '../queries/login';
import { useAuthContext } from '../context/AuthContext';
import { CaseInputType, useCaseIntake } from '../context/CaseIntakeContext';
import { usePersistentNavigate } from '../utils/navigation';
import { UserType } from '../schema';

const LoginContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  backgroundColor: '#f8fafc',
  padding: '16px',
});

const LoginCard = styled(Paper)({
  padding: '40px',
  width: '100%',
  maxWidth: '400px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: '8px',
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
});

const Login: React.FC = () => {
  const navigate = usePersistentNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordStep, setIsPasswordStep] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const { login, userType } = useAuthContext();
  const { caseContextText, caseContextJson, caseInputType } = useCaseIntake();


  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordStep) {
      // First step - email entered, now show password field
      setIsPasswordStep(true);
    } else {
      // Second step - attempt login with email and password
      try {
        // Clear any previous errors
        setPasswordError('');

        // Attempt login
        const response = await loginQuery(email, password);

        // Check if login was successful
        if (response && response.accessToken) {
          login({
            accessToken: response.accessToken,
            acceptedTermsOfService: response.acceptedTermsOfService,
            registrationComplete: response.registrationComplete,
            userType: response.userType,
            internalFlag: response.internalFlag
          })

          const isAdmin = userType === UserType.ADMIN || sessionStorage.getItem('userType') === UserType.ADMIN.toString();
          const isReviewer = response.userType === UserType.REVIEWER;

          // Handle navigation based on state
          // Only providers need to complete registration, not reviewers or admins
          if (!response.registrationComplete && !isAdmin && !isReviewer) {
            navigate('/complete-registration');
          } else if (isReviewer) {
            navigate('/reviewer');
          } else if (caseContextText || caseContextJson || caseInputType == CaseInputType.FILE) {
            navigate('/case-report');
          } else {
            navigate('/');
          }
        } else {
          // If login returned without an accessToken, it's an error
          setPassword(''); // Clear password field
          setPasswordError('Entered incorrect password.');
        }
      } catch (error) {
        console.error("Login failed:", error);

        // Clear password but keep email and stay on password step
        setPassword('');
        setPasswordError('Entered incorrect password.');
      }
    }
  };

  const handleBackToEmail = () => {
    setIsPasswordStep(false);
  };

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <LoginContainer maxWidth={false}>
      <LoginCard elevation={0}>
        {/* PrecisCa AI Logo/Title as a link back to landing page */}
        <RouterLink
          to="/"
          style={{ textDecoration: 'none' }}
        >
          <Box
            component="img"
            src="/ai logo no background.png"
            alt="Precisca AI"
            sx={{
              height: { xs: 48, sm: 64 },
              width: 'auto',
              display: 'block',
              mb: 2,
              cursor: 'pointer'
            }}
          />
        </RouterLink>

        <Typography
          variant="body1"
          sx={{
            mb: 1,
            mt: 3,
            color: '#4a5568',
            textAlign: 'center'
          }}
        >
          {isPasswordStep
            ? "Please enter your password to continue."
            : "Please log in or register to continue."}
        </Typography>

        {/* Form */}
        <Box
          component="form"
          onSubmit={handleContinue}
          sx={{ width: '100%' }}
        >
          {/* Email Field - always shown */}
          <TextField
            fullWidth
            id="email"
            label="Email address"
            variant="outlined"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            disabled={isPasswordStep}
            InputLabelProps={{ required: false }}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#cbd5e0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4299e1',
                },
              },
            }}
          />

          {/* Password Field - shown only on second step */}
          {isPasswordStep && (
            <>
              <TextField
                fullWidth
                id="password"
                label="Password"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                }}
                margin="normal"
                required
                error={!!passwordError}
                autoFocus
                InputLabelProps={{ required: false }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handlePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: passwordError ? 0.5 : 2,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: passwordError ? '#f56565' : '#e2e8f0',
                    },
                    '&:hover fieldset': {
                      borderColor: passwordError ? '#f56565' : '#cbd5e0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: passwordError ? '#f56565' : '#4299e1',
                    },
                  },
                }}
              />
              {passwordError && (
                <FormHelperText
                  error
                  sx={{
                    ml: 1.5,
                    mt: -0.5,
                    mb: 1.5,
                    fontSize: '0.8rem',
                    fontWeight: 500
                  }}
                >
                  {passwordError}
                </FormHelperText>
              )}
            </>
          )}

          {/* Continue Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              bgcolor: '#4299e1',
              '&:hover': {
                bgcolor: '#2c5282',
              },
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: 'none',
              borderRadius: '4px',
            }}
          >
            Continue
          </Button>

          {/* Different links/buttons based on step */}
          {isPasswordStep ? (
            <>
              {/* "Use different email" button */}
              <Button
                fullWidth
                variant="outlined"
                onClick={handleBackToEmail}
                sx={{
                  mb: 4,
                  py: 1.5,
                  color: '#718096',
                  borderColor: '#e2e8f0',
                  backgroundColor: '#f7fafc',
                  '&:hover': {
                    backgroundColor: '#edf2f7',
                    borderColor: '#cbd5e0',
                  },
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: 'none',
                  borderRadius: '4px',
                }}
              >
                Use a different email
              </Button>

              {/* Forgot password text */}
              <Typography variant="body2" sx={{ color: '#718096', textAlign: 'center' }}>
                Forgot password? (Coming soon)
              </Typography>
            </>
          ) : (
            <>
              {/* First step - show Sign up link */}
              <Typography variant="body2" sx={{ mb: 4, color: '#4a5568', textAlign: 'center' }}>
                Don't have an account?{' '}
                <RouterLink
                  to="/register"
                  style={{ textDecoration: 'none' }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: '#4299e1',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Sign up
                  </Typography>
                </RouterLink>
              </Typography>

              {/* OR Divider */}
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', margin: '24px 0' }}>
                <Box sx={{ flexGrow: 1, borderBottom: '1px solid #e2e8f0' }} />
                <Typography
                  variant="body2"
                  sx={{
                    px: 2,
                    color: '#718096',
                    fontWeight: 500,
                  }}
                >
                  OR
                </Typography>
                <Box sx={{ flexGrow: 1, borderBottom: '1px solid #e2e8f0' }} />
              </Box>

              {/* Google SSO Button */}
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleIcon />}
                disabled
                sx={{
                  mt: 1,
                  py: 1.2,
                  color: '#4a5568',
                  borderColor: '#e2e8f0',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  fontWeight: 400,
                  '&:hover': {
                    borderColor: '#cbd5e0',
                    bgcolor: '#f7fafc',
                  },
                }}
              >
                Continue with Google <Typography component="span" sx={{ ml: 1, color: '#718096', fontSize: '0.85rem' }}>(Coming Soon)</Typography>
              </Button>
            </>
          )}
        </Box>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;